import type { FileNode } from '@ncoder/ui';
import type { RulesScope } from './types';

/**
 * Rules files are synthetic explorer nodes. Their ids carry this prefix, which is how every
 * seam (open, save, read-only, suppressed branch props) tells them from metadata items.
 */
export const RULES_ID_PREFIX = '__rules__/';
export const RULES_FOLDER_ID = '__rules__';

export const RULES_FILE_NAME: Record<RulesScope, string> = {
  org: 'ORG_RULES.md',
  mine: 'MY_RULES.md',
};

export function rulesFileId(scope: RulesScope): string {
  return `${RULES_ID_PREFIX}${scope}`;
}

export function isRulesFileId(id: string | null | undefined): boolean {
  return typeof id === 'string' && id.startsWith(RULES_ID_PREFIX);
}

/** Scope for a rules file id, or null when the id is not a (known) rules file. */
export function rulesScopeOf(id: string | null | undefined): RulesScope | null {
  if (!isRulesFileId(id)) return null;
  const tail = (id as string).slice(RULES_ID_PREFIX.length);
  return tail === 'org' || tail === 'mine' ? tail : null;
}

export function rulesFileNode(scope: RulesScope): FileNode {
  return {
    id: rulesFileId(scope),
    name: RULES_FILE_NAME[scope],
    type: 'file',
    path: `/${RULES_FOLDER_ID}/${RULES_FILE_NAME[scope]}`,
  };
}

/** The pinned "Rules" folder, with both files as static children. */
export function rulesFolderNode(): FileNode {
  return {
    id: RULES_FOLDER_ID,
    name: 'Rules',
    type: 'folder',
    path: `/${RULES_FOLDER_ID}`,
    count: 2,
    children: [rulesFileNode('org'), rulesFileNode('mine')],
  };
}

/** Prepend the pinned Rules folder to the metadata tree (always first, never filtered). */
export function withRulesFolder(files: FileNode[]): FileNode[] {
  return [rulesFolderNode(), ...files];
}

/**
 * Whether the caller may edit a rules file. The server enforces the same rule; this only
 * keeps the editor honest. MY_RULES.md is always the caller's own.
 */
export function canEditRules(
  scope: RulesScope,
  role: 'OWNER' | 'ADMIN' | 'MEMBER' | null | undefined,
): boolean {
  if (scope === 'mine') return true;
  return role === 'OWNER' || role === 'ADMIN';
}

export type RulesSaveFailure = 'conflict' | 'too_large' | 'forbidden' | 'error';

/** Classify a failed save by HTTP status, never by message text. */
export function classifyRulesSaveError(error: unknown): RulesSaveFailure {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  if (status === 409) return 'conflict';
  if (status === 413) return 'too_large';
  if (status === 403) return 'forbidden';
  return 'error';
}

/** `max_bytes` from a 413 body (`RULES_TOO_LARGE`), when present. */
export function maxBytesFromError(error: unknown): number | null {
  const v = (error as { response?: { data?: { max_bytes?: unknown } } } | null)?.response?.data?.max_bytes;
  return typeof v === 'number' ? v : null;
}

export function rulesSaveMessage(
  kind: RulesSaveFailure,
  scope: RulesScope,
  maxBytes: number | null = null,
): string {
  switch (kind) {
    case 'conflict':
      return 'This rules file changed elsewhere \u2014 reload to see the latest version.';
    case 'too_large':
      return maxBytes !== null
        ? `This rules file is too large (limit ${maxBytes} bytes). Shorten it and save again.`
        : 'This rules file is too large. Shorten it and save again.';
    case 'forbidden':
      return scope === 'org'
        ? `Only organization admins can edit ${RULES_FILE_NAME.org}.`
        : "You don't have access to this organization's rules.";
    default:
      return 'Could not save the rules file.';
  }
}
